import { useState } from 'react';
import { Link } from 'react-router-dom';
import { forgotPassword } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { 
    Loader2, 
    Mail, 
    ArrowLeft,
    CheckCircle,
    Ticket
} from 'lucide-react';

export default function ForgotPasswordPage() {
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            await forgotPassword(email);
            setSuccess(true);
        } catch (err) {
            setError(err.response?.data?.detail || 'Errore durante la richiesta');
        } finally {
            setLoading(false);
        }
    };

    if (success) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center p-4">
                <Card className="w-full max-w-md border-slate-200">
                    <CardContent className="pt-6">
                        <div className="text-center">
                            <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                                <CheckCircle className="w-8 h-8 text-emerald-600" />
                            </div>
                            <h2 className="text-2xl font-bold text-slate-900 mb-2">Email Inviata!</h2>
                            <p className="text-slate-600 mb-6">
                                Se l'indirizzo <strong>{email}</strong> è associato a un account, 
                                riceverai un'email con le istruzioni per reimpostare la password.
                            </p>
                            <Alert className="mb-6 bg-blue-50 border-blue-200 text-left">
                                <Mail className="h-4 w-4 text-blue-600" />
                                <AlertDescription className="text-blue-800">
                                    Controlla anche la cartella spam. Il link scadrà tra 1 ora.
                                </AlertDescription>
                            </Alert>
                            <Link to="/login">
                                <Button className="w-full bg-indigo-500 hover:bg-indigo-600">
                                    <ArrowLeft className="w-4 h-4 mr-2" />
                                    Torna al Login
                                </Button>
                            </Link>
                        </div>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center p-4">
            <Card className="w-full max-w-md border-slate-200">
                <CardHeader className="text-center pb-2">
                    <div className="w-14 h-14 rounded-xl bg-indigo-500 flex items-center justify-center mx-auto mb-4">
                        <Ticket className="w-7 h-7 text-white" />
                    </div>
                    <CardTitle className="text-2xl">Password Dimenticata</CardTitle>
                    <CardDescription>
                        Inserisci la tua email per ricevere un link di reset
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        {error && (
                            <Alert variant="destructive">
                                <AlertDescription>{error}</AlertDescription>
                            </Alert>
                        )}
                        
                        <div className="space-y-2">
                            <Label htmlFor="email">Email</Label>
                            <Input
                                id="email"
                                type="email"
                                placeholder="mario.rossi@email.com"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                data-testid="forgot-email-input"
                            />
                        </div>

                        <Button 
                            type="submit" 
                            className="w-full bg-indigo-500 hover:bg-indigo-600" 
                            disabled={loading || !email}
                            data-testid="forgot-submit-btn"
                        >
                            {loading ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                                <Mail className="w-4 h-4 mr-2" />
                            )}
                            Invia Link di Reset
                        </Button>

                        <div className="text-center pt-2">
                            <Link 
                                to="/login" 
                                className="text-sm text-indigo-600 hover:text-indigo-800"
                            >
                                <ArrowLeft className="w-3 h-3 inline mr-1" />
                                Torna al Login
                            </Link>
                        </div>
                    </form>
                </CardContent>
            </Card>
        </div>
    );
}
